// İSG Portal — KKD (Kişisel Koruyucu Donanım — tenant-scoped) katalog API çağrıları + tipleri.
// **İSG uzmanı / firma admin tarafı** (web-expert): firma kendi KKD kataloğunu
// (`ProtectiveEquipment`) tanımlar — genel (bizim) varsayılan liste + firma özel öge. Her KKD
// ögesi bir **iş izni tipine** (§22) bağlanabilir (`PermitTypeProtectiveEquipment`); izin detayında
// hem İSG uzmanı hem taşeron KKD listesini **read-only** görür (donanımı taşeron getirir).
//
// KAYNAK: api-contract.md §24 (KKD). Backend kanonu; burada **varsayım YOK** — endpoint yolları,
// istek/yanıt alanları ve kimlik kuralları (Result unwrap `value`; Create → `value.uuid` + 201; liste
// → `value` + `total`/`filteredTotal`) doğrudan sözleşmedendir. Serileştirme camelCase (§1).
//
// **Tenant izolasyonu (§4b/§24).** Tüm uçlar çağıranın firmasına (token `CompanyId`) kapanır;
// istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın ögesi hiçbir koşulda
// görünmez/güncellenmez/silinmez (yok gibi `404 PROTECTIVE_EQUIPMENT_NOT_FOUND`). Silme
// **soft-delete**'tir (tipe önceden bağlanmış izinler etkilenmez — bağ replace ile yönetilir).
// Uçlar `[ModuleAction("permit-types", …)]` (iş izni tipi kataloğuyla **aynı grant** — yeni grant
// gerekmez); ayrı tek-segment route (`/protective-equipment`).

import { api } from './client';
import type { Empty } from './envelope';

/**
 * KKD ögesi — api-contract §24 (`ProtectiveEquipmentDto`). `isDefault` = **genel (bizim) varsayılan
 * liste** ögesi mi (**istemciden set EDİLMEZ** — yalnız seed `true` yazar; CRUD hep `false`). Genel
 * öge de düzenlenebilir/pasifleştirilebilir. Sıralı iç `Id` istemciye **sızmaz** (yalnız `uuid`).
 * `createdAt` UTC ISO (§4).
 */
export interface ProtectiveEquipmentDto {
  uuid: string;
  name: string;
  description: string | null;
  /** Genel (bizim) varsayılan liste ögesi mi — **istemciden set edilmez**; yalnız rozet/görünüm (§24). */
  isDefault: boolean;
  isActive: boolean;
  createdAt: string | null;
}

/**
 * KKD kısa referansı — api-contract §24 (`ProtectiveEquipmentRefDto`). Tip detayı
 * (`PermitTypeDetailDto.requiredProtectiveEquipment`) + izin detayı
 * (`WorkPermitDetailDto.requiredProtectiveEquipment`) bu shape'i taşır.
 */
export interface ProtectiveEquipmentRefDto {
  uuid: string;
  name: string;
}

/**
 * KKD oluşturma isteği — api-contract §24.1 (`POST /protective-equipment`). `name` zorunlu (boş →
 * `400 VALIDATION_FAILED`); yeni öge `isActive = true`, `isDefault = false` (istekte taşınmaz).
 * **`isDefault` GÖNDERİLMEZ.**
 */
export interface ProtectiveEquipmentCreateRequest {
  name: string;
  description?: string | null;
}

/**
 * KKD güncelleme isteği — api-contract §24.1 (`PUT /protective-equipment/{uuid}`). Ad/açıklama/
 * aktiflik güncellenir; **`isDefault` istemciden set edilmez** (korunur — genel öge de düzenlenebilir).
 */
export interface ProtectiveEquipmentUpdateRequest {
  name: string;
  description?: string | null;
  isActive: boolean;
}

/**
 * KKD liste/filtre parametreleri — api-contract §24.1 (`GET /protective-equipment`) + §3 paging.
 * `isActive` (verilmezse hepsi), `search` (ad substring, case-insensitive). Sıra backend'de ad artan.
 */
export interface ProtectiveEquipmentListParams {
  isActive?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * `GET /protective-equipment` (`list`) — firmanın KKD kataloğu (sayfalı + filtreli). Sıra ad artan
 * (istemci sort göndermez). `isActive` verilmezse hepsi döner.
 */
export function listProtectiveEquipment(params: ProtectiveEquipmentListParams = {}) {
  return api.list<ProtectiveEquipmentDto>('protective-equipment', {
    searchParams: {
      isActive: params.isActive,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `POST /protective-equipment` (`create`) — KKD ögesi oluşturur (`isActive = true`,
 * `isDefault = false`); yeni kimlik `value.uuid` (201). `name` boş → `400 VALIDATION_FAILED`.
 */
export function createProtectiveEquipment(body: ProtectiveEquipmentCreateRequest) {
  return api.post<ProtectiveEquipmentDto>('protective-equipment', { json: body });
}

/**
 * `PUT /protective-equipment/{uuid}` (`update`) — ad/açıklama/aktiflik günceller (`isDefault`
 * korunur). Yok/kapsam dışı → `404 PROTECTIVE_EQUIPMENT_NOT_FOUND`.
 */
export function updateProtectiveEquipment(uuid: string, body: ProtectiveEquipmentUpdateRequest) {
  return api.put<ProtectiveEquipmentDto>(`protective-equipment/${uuid}`, { json: body });
}

/**
 * `DELETE /protective-equipment/{uuid}` (`delete`) — ögeyi **soft-delete** eder (tipe önceden
 * bağlanmış izinler etkilenmez). Yok/kapsam dışı → `404 PROTECTIVE_EQUIPMENT_NOT_FOUND`.
 */
export function deleteProtectiveEquipment(uuid: string) {
  return api.delete<Empty>(`protective-equipment/${uuid}`);
}
